import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL, getAuthHeader, images } from '../../utils/api';
import { usePreferences } from '../../hooks/usePreferences';
import BaseTable from '../../components/shared/table/BaseTable';
import { TableProvider, TableContext } from '../../components/shared/table/context/TableContext';
import { SystemAuctionLotsToolbar } from './SystemAuctionLotsToolbar';

// Content component that uses TableContext
function SystemAuctionLotsContent({ columns, data, onAdd, onScrape, loading, error, selectedItems, onSelectionChange }) {
  const { setColumns } = useContext(TableContext);

  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);

  return (
    <>
      <SystemAuctionLotsToolbar
        columns={columns}
        data={data}
        onScrape={onScrape}
        onAdd={onAdd}
      />
      <BaseTable
        data={data}
        columns={columns}
        loading={loading}
        error={error}
        selectedItems={selectedItems}
        onSelectionChange={onSelectionChange}
      />
    </>
  );
}

// Button component that uses TableContext
function UseFilteredLotsButton({ lots }) {
  const tableContext = React.useContext(TableContext);
  
  if (!tableContext) {
    console.error('UseFilteredLotsButton must be used within a TableProvider');
    return null;
  }

  const { filterRows, sortRows, applyFilters } = tableContext;
  
  // Get filtered lots
  const filteredData = filterRows(lots);
  const processedData = sortRows(applyFilters(filteredData));

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => {
          // Extract lot numbers and join with commas
          const lotNumbers = processedData.map(lot => lot.lot_number).join(',');
          
          // Set the value in the input
          const lotNumbersInput = document.querySelector('input[name="lotNumbers"]');
          if (lotNumbersInput) {
            lotNumbersInput.value = lotNumbers;
          }
        }}
        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
      >
        Use Filtered Lots
      </button>
      <div className="text-sm text-gray-500 text-center">
        {processedData.length} lot{processedData.length !== 1 ? 's' : ''} selected
      </div>
    </div>
  );
}

// Configure axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// Add auth header interceptor
api.interceptors.request.use(async (config) => {
  const headers = await getAuthHeader(config.method);
  config.headers = { ...config.headers, ...Object.fromEntries(headers.entries()) };
  return config;
});

// Form for creating/editing lots
function LotForm({ lot, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    lot_number: lot?.lot_number || '',
    country: lot?.country || '',
    year: lot?.year || '',
    denomination: lot?.denomination || '',
    variety: lot?.variety || '',
    certification_service_id: lot?.certification_service_id || '',
    certification_number: lot?.certification_number || '',
    pcgs_number: lot?.pcgs_number || '',
    grade: lot?.grade || '',
    status: lot?.status || 'listed',
    metal: lot?.metal || '',
    composition: lot?.composition || '',
    weight: lot?.weight || '',
    diameter: lot?.diameter || '',
    edge: lot?.edge || '',
    mintage: lot?.mintage || '',
    population: lot?.population || '',
    pop_higher: lot?.pop_higher || '',
    price_guide_value: lot?.price_guide_value || '',
    mint_mark: lot?.mint_mark || '',
    cac_sticker: lot?.cac_sticker || 'none',
    opening_bid: lot?.opening_bid || '',
    winning_bid: lot?.winning_bid || '',
    provenance: lot?.provenance || '',
    images: lot?.images || []
  });

  const [error, setError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.lot_number || !formData.country || !formData.year || !formData.denomination || !formData.status) {
      setError('Please fill in all required fields');
      return;
    }
    // Exclude images from the form data
    const { images, ...submitData } = formData;
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Lot Number *
          </label>
          <input
            type="text"
            value={formData.lot_number}
            onChange={e => setFormData(prev => ({ ...prev, lot_number: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Country *
          </label>
          <input
            type="text"
            value={formData.country}
            onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Year *
          </label>
          <input
            type="text"
            value={formData.year}
            onChange={e => setFormData(prev => ({ ...prev, year: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Denomination *
          </label>
          <input
            type="text"
            value={formData.denomination}
            onChange={e => setFormData(prev => ({ ...prev, denomination: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Status *
          </label>
          <select
            value={formData.status}
            onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="listed">Listed</option>
            <option value="sold">Sold</option>
            <option value="unsold">Unsold</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Variety
          </label>
          <input
            type="text"
            value={formData.variety}
            onChange={e => setFormData(prev => ({ ...prev, variety: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Minor Variety
          </label>
          <input
            type="text"
            value={formData.minor_variety}
            onChange={e => setFormData(prev => ({ ...prev, minor_variety: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Die Variety
          </label>
          <input
            type="text"
            value={formData.die_variety}
            onChange={e => setFormData(prev => ({ ...prev, die_variety: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Reference #
          </label>
          <input
            type="text"
            value={formData.reference_number}
            onChange={e => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Certification Service
          </label>
          <input
            type="text"
            value={formData.certification_service_id}
            onChange={e => setFormData(prev => ({ ...prev, certification_service_id: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Certification #
          </label>
          <input
            type="text"
            value={formData.certification_number}
            onChange={e => setFormData(prev => ({ ...prev, certification_number: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            PCGS #
          </label>
          <input
            type="text"
            value={formData.pcgs_number}
            onChange={e => setFormData(prev => ({ ...prev, pcgs_number: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Grade
          </label>
          <input
            type="text"
            value={formData.grade}
            onChange={e => setFormData(prev => ({ ...prev, grade: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            CAC Sticker
          </label>
          <select
            value={formData.cac_sticker}
            onChange={e => setFormData(prev => ({ ...prev, cac_sticker: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="none">None</option>
            <option value="gold">Gold</option>
            <option value="green">Green</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Metal
          </label>
          <input
            type="text"
            value={formData.metal}
            onChange={e => setFormData(prev => ({ ...prev, metal: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Composition %
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formData.composition}
            onChange={e => setFormData(prev => ({ ...prev, composition: e.target.value ? parseFloat(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Weight (g)
          </label>
          <input
            type="number"
            step="0.001"
            min="0"
            value={formData.weight}
            onChange={e => setFormData(prev => ({ ...prev, weight: e.target.value ? parseFloat(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Diameter (mm)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.diameter}
            onChange={e => setFormData(prev => ({ ...prev, diameter: e.target.value ? parseFloat(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Edge
          </label>
          <input
            type="text"
            value={formData.edge}
            onChange={e => setFormData(prev => ({ ...prev, edge: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Mint Mark
          </label>
          <input
            type="text"
            value={formData.mint_mark}
            onChange={e => setFormData(prev => ({ ...prev, mint_mark: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Mintage
          </label>
          <input
            type="text"
            value={formData.mintage}
            onChange={e => setFormData(prev => ({ ...prev, mintage: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Population
          </label>
          <input
            type="number"
            min="0"
            value={formData.population}
            onChange={e => setFormData(prev => ({ ...prev, population: e.target.value ? parseInt(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Pop Higher
          </label>
          <input
            type="number"
            min="0"
            value={formData.pop_higher}
            onChange={e => setFormData(prev => ({ ...prev, pop_higher: e.target.value ? parseInt(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Assayer
          </label>
          <input
            type="text"
            value={formData.assayer}
            onChange={e => setFormData(prev => ({ ...prev, assayer: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Provenance
          </label>
          <input
            type="text"
            value={formData.provenance}
            onChange={e => setFormData(prev => ({ ...prev, provenance: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Opening Bid
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.opening_bid}
            onChange={e => setFormData(prev => ({ ...prev, opening_bid: e.target.value ? parseFloat(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Winning Bid
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.winning_bid}
            onChange={e => setFormData(prev => ({ ...prev, winning_bid: e.target.value ? parseFloat(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Price Guide Value
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price_guide_value}
            onChange={e => setFormData(prev => ({ ...prev, price_guide_value: e.target.value ? parseFloat(e.target.value) : '' }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {lot ? 'Update' : 'Create'} Lot
        </button>
      </div>
    </form>
  );
}

// Main lots manager component
export function SystemAuctionLots({ auctionId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrapingModalOpen, setIsScrapingModalOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const queryClient = useQueryClient();

  const handleScrape = async (lotNumbers, skipPcgsApi, finalScrape) => {
    try {
      // Get CSRF token from cookie
      const match = document.cookie.match(/(?:^|;\s*)csrf-token=([^;]*)/);
      const csrfToken = match ? decodeURIComponent(match[1]) : null;

      // Get form data
      const formData = new FormData(document.querySelector('form'));
      const skipImageProcessing = formData.get('skipImageProcessing') === 'on';

      // Start scraping
      const { data } = await api.get(
        `/auction-scraping/auctions/${auctionId}/lots`,
        {
          params: {
            lotNumbers: encodeURIComponent(lotNumbers),
            skipPcgsApi,
            skipImageProcessing,
            finalScrape,
            csrf: csrfToken
          }
        }
      );

      queryClient.invalidateQueries(['admin', 'system-auctions', auctionId, 'lots']);
      setIsScrapingModalOpen(false);
    } catch (error) {
      console.error('Error scraping lots:', error);
    }
  };

  // Fetch auction details including lots
  const { data: auction, isLoading: dataLoading, error: dataError } = useQuery({
    queryKey: ['admin', 'system-auctions', auctionId, 'lots'],
    queryFn: async () => {
      const { data } = await api.get(`/admin/system-auctions/${auctionId}`);
      return data;
    }
  });

  const { preferences, loading: prefsLoading, error: prefsError } = usePreferences('system_auction_lots');
  
  const isLoading = dataLoading || prefsLoading;
  const error = dataError || prefsError;

  const lots = useMemo(() => {
    const unsortedLots = auction?.lots || [];
    // If no lots, return empty array
    if (unsortedLots.length === 0) return [];
    
    // Sort by lot_number ascending by default
    return [...unsortedLots].sort((a, b) => {
      const numA = parseFloat(a.lot_number) || -Infinity;
      const numB = parseFloat(b.lot_number) || -Infinity;
      return numA - numB;
    });
  }, [auction?.lots]);

  // Create lot
  const createMutation = useMutation({
    mutationFn: async (lot) => {
      const { data } = await api.post(`/admin/system-auctions/${auctionId}/lots`, lot);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions', auctionId, 'lots']);
      setIsModalOpen(false);
    }
  });

  // Update lot
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...lot }) => {
      // Convert empty strings to null for numeric fields
      const cleanedLot = {
        ...lot,
        opening_bid: lot.opening_bid === '' ? null : lot.opening_bid,
        winning_bid: lot.winning_bid === '' ? null : lot.winning_bid,
        composition: lot.composition === '' ? null : lot.composition,
        weight: lot.weight === '' ? null : lot.weight,
        diameter: lot.diameter === '' ? null : lot.diameter,
        population: lot.population === '' ? null : lot.population,
        pop_higher: lot.pop_higher === '' ? null : lot.pop_higher,
        price_guide_value: lot.price_guide_value === '' ? null : lot.price_guide_value
      };
      const { data } = await api.put(`/admin/system-auctions/${auctionId}/lots/${id}`, cleanedLot);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions', auctionId, 'lots']);
      setIsModalOpen(false);
      setSelectedLot(null);
    }
  });

  // Delete lot
  const deleteMutation = useMutation({
    mutationFn: async (lotId) => {
      await api.delete(`/admin/system-auctions/${auctionId}/lots/${lotId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin', 'system-auctions', auctionId, 'lots']);
    }
  });

  const columns = React.useMemo(() => [
    {
      headerName: 'Images',
      field: 'images',
      renderCell: (item) => {
        const { preferences } = useContext(TableContext);
        const density = preferences?.density || 'standard';
        
        // Define heights based on density
        const heightClass = {
          compact: 'h-8',
          standard: 'h-12',
          comfortable: 'h-16'
        }[density];

        return (
          <div className="flex gap-1 items-center">
            {item.images?.sort((a, b) => a.display_order - b.display_order).map((image, index) => {
              // Ensure we have the source from the database
              const imageSource = image.source || (image.thumbnail_path.includes('/cert/') ? 'cert_lookup' : null);
              
              const imageUrl = images.getImage(image.thumbnail_path, imageSource);
              
              return (
                <img
                  key={index}
                  src={imageUrl}
                  alt={`Image ${index + 1}`}
                  className={`${heightClass} w-auto object-contain rounded border border-gray-200`}
                  title={image.image_type}
                />
              );
            })}
          </div>
        );
      }
    },
    {
      headerName: 'Metadata',
      field: 'metadata',
      renderCell: (item) => {
        if (!item.metadata) return '-';
        return (
          <div className="text-sm w-full overflow-x-auto">
            <div className="flex items-center gap-4 whitespace-nowrap min-w-fit">
              {item.metadata.raw_title && (
                <span className="shrink-0">
                  <span className="font-semibold">Raw:</span> {item.metadata.raw_title}
                </span>
              )}
              {item.metadata.translated_title && (
                <span className="shrink-0">
                  <span className="font-semibold">Trans:</span> {item.metadata.translated_title}
                </span>
              )}
              {item.metadata.raw_description && (
                <span className="shrink-0">
                  <span className="font-semibold">Raw Desc:</span> {item.metadata.raw_description}
                </span>
              )}
              {item.metadata.translated_description && (
                <span className="shrink-0">
                  <span className="font-semibold">Trans Desc:</span> {item.metadata.translated_description}
                </span>
              )}
              {item.metadata.lot_url && (
                <span className="shrink-0">
                  <span className="font-semibold">URL:</span>{' '}
                  <a 
                    href={item.metadata.lot_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {item.metadata.lot_url}
                  </a>
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      headerName: 'Lot #',
      field: 'lot_number'
    },
    {
      headerName: 'Country',
      field: 'country'
    },
    {
      headerName: 'Year',
      field: 'year'
    },
    {
      headerName: 'Denomination',
      field: 'denomination'
    },
    {
      headerName: 'Variety',
      field: 'variety'
    },
    {
      headerName: 'Cert Service',
      field: 'certification_service_id'
    },
    {
      headerName: 'Cert #',
      field: 'certification_number'
    },
    {
      headerName: 'PCGS #',
      field: 'pcgs_number'
    },
    {
      headerName: 'Grade',
      field: 'grade'
    },
    {
      headerName: 'CAC',
      field: 'cac_sticker',
      renderCell: (item) => item.cac_sticker === 'none' ? '-' : item.cac_sticker.toUpperCase()
    },
    {
      headerName: 'Opening Bid',
      field: 'opening_bid',
      renderCell: (item) => item.opening_bid ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: auction?.currency || 'USD',
        minimumFractionDigits: auction?.currency === 'JPY' ? 0 : 2
      }).format(item.opening_bid) : '-'
    },
    {
      headerName: 'Winning Bid',
      field: 'winning_bid',
      renderCell: (item) => item.winning_bid ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: auction?.currency || 'USD',
        minimumFractionDigits: auction?.currency === 'JPY' ? 0 : 2
      }).format(item.winning_bid) : '-'
    },
    {
      headerName: 'Price Guide',
      field: 'price_guide_value',
      renderCell: (item) => item.price_guide_value ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(item.price_guide_value) : '-'
    },
    {
      headerName: 'Metal',
      field: 'metal'
    },
    {
      headerName: 'Composition',
      field: 'composition',
      renderCell: (item) => item.composition ? `${item.composition}%` : '-'
    },
    {
      headerName: 'Weight',
      field: 'weight',
      renderCell: (item) => item.weight ? `${item.weight}g` : '-'
    },
    {
      headerName: 'Diameter',
      field: 'diameter',
      renderCell: (item) => item.diameter ? `${item.diameter}mm` : '-'
    },
    {
      headerName: 'Edge',
      field: 'edge'
    },
    {
      headerName: 'Mint Mark',
      field: 'mint_mark'
    },
    {
      headerName: 'Mintage',
      field: 'mintage'
    },
    {
      headerName: 'Population',
      field: 'population'
    },
    {
      headerName: 'Pop Higher',
      field: 'pop_higher'
    },
    {
      headerName: 'Minor Variety',
      field: 'minor_variety'
    },
    {
      headerName: 'Die Variety',
      field: 'die_variety'
    },
    {
      headerName: 'Reference #',
      field: 'reference_number'
    },
    {
      headerName: 'Assayer',
      field: 'assayer'
    },
    {
      headerName: 'Provenance',
      field: 'provenance'
    },
    {
      headerName: 'Status',
      field: 'status',
      renderCell: (item) => (
        <span className={`capitalize ${
          item.status === 'sold' ? 'text-green-600' :
          item.status === 'unsold' ? 'text-red-600' :
          item.status === 'withdrawn' ? 'text-gray-600' :
          'text-blue-600'
        }`}>
          {item.status}
        </span>
      )
    },
    {
      headerName: 'Actions',
      field: 'actions',
      renderCell: (item) => (
        <div className="flex space-x-2" onClick={(e) => e.stopPropagation()} data-interactive="true">
          <button
            className="px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedLot(item);
              setIsModalOpen(true);
            }}
          >
            Edit
          </button>
          <button
            className="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this lot?')) {
                deleteMutation.mutate(item.id);
              }
            }}
          >
            Delete
          </button>
        </div>
      )
    }
  ], [deleteMutation]);

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col">
      <div className="bg-white rounded-lg shadow flex-1 min-h-0 overflow-hidden relative pb-12">
        <TableProvider
          type="system_auction_lots"
          initialState={{
            loading: isLoading,
            error: error,
            preferences: preferences || {
              density: 'standard',
              columnWidths: {
                images: 150,
                metadata: 300,
                lot_number: 100,
                country: 120,
                year: 100,
                denomination: 150,
                variety: 150,
                certification_service_id: 120,
                certification_number: 120,
                pcgs_number: 100,
                grade: 100,
                cac_sticker: 80,
                opening_bid: 120,
                winning_bid: 120,
                price_guide_value: 120,
                metal: 100,
                composition: 100,
                weight: 100,
                diameter: 100,
                edge: 100,
                mint_mark: 100,
                mintage: 120,
                population: 100,
                pop_higher: 100,
                minor_variety: 150,
                die_variety: 150,
                reference_number: 120,
                assayer: 120,
                provenance: 200,
                status: 100,
                actions: 150
              }
            }
          }}
        >
          <SystemAuctionLotsContent
            columns={columns}
            data={lots || []}
            onAdd={() => {
              setSelectedLot(null);
              setIsModalOpen(true);
            }}
            onScrape={() => setIsScrapingModalOpen(true)}
            loading={isLoading}
            error={error}
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
          />
        </TableProvider>
      </div>

      {/* Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
              setIsModalOpen(false);
              setSelectedLot(null);
            }}></div>
            <div className="relative bg-white rounded-lg max-w-lg w-full">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium">
                  {selectedLot ? 'Edit Lot' : 'Add Lot'}
                </h3>
              </div>
              <div className="p-6">
                <LotForm
                  lot={selectedLot}
                  onSubmit={(formData) => {
                    if (selectedLot) {
                      updateMutation.mutate({ id: selectedLot.id, ...formData });
                    } else {
                      createMutation.mutate(formData);
                    }
                  }}
                  onCancel={() => {
                    setIsModalOpen(false);
                    setSelectedLot(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scraping Modal */}
      {isScrapingModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75" 
              onClick={() => {
                setIsScrapingModalOpen(false);
                queryClient.invalidateQueries(['admin', 'system-auctions', auctionId, 'lots']);
              }}
            ></div>
            <div className="relative bg-white rounded-lg max-w-lg w-full">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-medium">Scrape Auction Lots</h3>
              </div>
              <div className="p-6">
                <TableProvider
                  type="system_auction_lots"
                  initialState={{
                    loading: false,
                    error: null,
                    preferences: {
                      density: 'standard',
                      columnWidths: {},
                      hiddenColumns: [],
                      filters: [],
                      sortConfig: [],
                      quickFilter: '',
                      pinnedColumns: [],
                      columnOrder: []
                    }
                  }}
                >
                  <div className="mb-4">
                    <UseFilteredLotsButton lots={lots} />
                  </div>
                </TableProvider>
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleScrape(
                      formData.get('lotNumbers'),
                      formData.get('skipPcgsApi') === 'on',
                      formData.get('finalScrape') === 'on',
                      formData.get('skipImageProcessing') === 'on'
                    );
                  }} 
                  className="space-y-4"
                >
                  {/* Form inputs */}
                  <div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Lot Numbers
                      </label>
                      <input
                        type="text"
                        name="lotNumbers"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="e.g. 1-5,8,10-15"
                        defaultValue={selectedItems.size > 0 ? 
                          [...selectedItems].map(id => lots.find(lot => String(lot.id) === String(id))?.lot_number).filter(Boolean).join(',') 
                          : ''}
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Enter individual lot numbers or ranges (e.g. 1-5,8,10-15). Maximum 100 lots.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="skipImageProcessing"
                          id="skipImageProcessing"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="skipImageProcessing" className="ml-2 block text-sm text-gray-900">
                          Skip Image Processing
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="skipPcgsApi"
                          id="skipPcgsApi"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="skipPcgsApi" className="ml-2 block text-sm text-gray-900">
                          Skip PCGS API calls
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="finalScrape"
                          id="finalScrape"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="finalScrape" className="ml-2 block text-sm text-gray-900">
                          Final scrape (hammer prices only)
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Modal footer */}
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setIsScrapingModalOpen(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Start Scraping
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
